/**
 * cn(): clsx + tailwind-merge for the `tw-` prefixed theme (plans/STARS_FOUNDATION.md R-F7).
 *
 * tailwind-merge 3 reads Tailwind 4 syntax (`tw:p-4`), not the Tailwind 3 prefix
 * (`tw-p-4`). So `tw-` tokens are un-prefixed before merging and re-prefixed after,
 * and the theme's own steps (text xs..3xl, radii, shadows, durations) are declared
 * so a later class replaces an earlier one in the same group.
 */
import { clsx } from 'clsx';
import { extendTailwindMerge, validators } from 'tailwind-merge';

const merge = extendTailwindMerge({
  // Tailwind 4 reads bare `outline` as width and `outline-solid` as style; in Tailwind 3
  // `outline` is outline-style: solid, so `outline` + `outline-2` must both survive.
  override: { classGroups: { 'outline-w': [{ outline: [validators.isNumber] }] } },
  extend: {
    theme: {
      text: ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'],
      radius: ['pill', 'pass', 'input', 'tile', 'tail', 'composer'],
      shadow: ['1', '2', '3'],
      'font-weight': ['regular'],
    },
    classGroups: {
      duration: [{ duration: ['fast', 'base', 'slow'] }],
      ease: [{ ease: ['out'] }],
      'outline-style': ['outline'],
    },
  },
});

// "hover:tw-bg-action" -> "hover:bg-action"; "-tw-my-3" (negative margin) -> "-my-3".
const strip = (token) => token.replace(/(^|:)(-?)tw-/, '$1$2');
const isOwn = (token) => /(^|:)-?tw-/.test(token);

export function cn(...inputs) {
  const tokens = clsx(inputs).split(/\s+/).filter(Boolean);
  const own = tokens.filter(isOwn);
  const other = tokens.filter((t) => !isOwn(t));
  const merged = merge(own.map(strip).join(' ')).split(' ').filter(Boolean);
  // Put the prefix back after the last variant colon (and after a negative sign).
  const back = merged.map((t) => {
    const i = t.lastIndexOf(':') + 1;
    const rest = t.slice(i);
    return rest.startsWith('-') ? `${t.slice(0, i)}-tw-${rest.slice(1)}` : `${t.slice(0, i)}tw-${rest}`;
  });
  return [...other, ...back].join(' ');
}
